"use client";

import React, { useEffect, useRef } from "react";
import { LoginForm } from "./LoginForm";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../Button";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { TruecallerLogin } from "./TruecallerLogin";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  AppRoutesEnum,
  CookiesEnum,
  cookiesValues,
  EventNamesEnum,
  SourcePagesEnum,
  sourcePagesValues,
  WindowEventListenersEnum,
} from "@/constants/constants";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { useRouter } from "next/navigation";
import { getClientSideCookieValue } from "@/utils/common.helpers";

export const LoginDrawer = ({
  isLoading,
  label,
  onOpenChange = () => {},
  setIsLoading = () => {},
  triggerComponent,
  loginSource = sourcePagesValues[SourcePagesEnum.LOGIN],
  open,
  onDrawerOpenChange,
  preventCloseOnOutsideClick = false,
  hideCloseButton = false,
  logTitle,
  logSubTitle,
  showLoginButton = true,
}: {
  isLoading: boolean;
  label?: string;
  onOpenChange?: () => void;
  setIsLoading?: (isLoading: boolean) => void;
  triggerComponent?: React.ReactNode;
  loginSource?: string;
  open?: boolean;
  onDrawerOpenChange?: (open: boolean) => void;
  preventCloseOnOutsideClick?: boolean;
  hideCloseButton?: boolean;
  logTitle?: string;
  logSubTitle?: string;
  showLoginButton?: boolean;
}) => {
  const { t } = useClientTranslations();
  const videoPlayerRef = useRef<HTMLVideoElement | null>(null);
  const { trackEvent } = useTrackingAnalytics();
  const router = useRouter();

  useEffect(() => {
    /*
     * We use querySelector to get the video player element by its class (.js-paywall-video-player)
     * because the video player is rendered as a sibling of the login drawer in the DOM tree.
     * Passing a reference or callback through React props is not feasible here, since the login drawer
     * and the video player are not in a direct parent-child relationship and may be managed by different
     * parts of the app. Using querySelector allows us to reliably access the video player element
     * from anywhere in the component tree, regardless of how the components are composed.
     */
    videoPlayerRef.current = document.querySelector(
      ".js-paywall-video-player",
    ) as HTMLVideoElement | null;
  }, []);

  useEffect(() => {
    if (open) {
      trackEvent({
        eventName: EventNamesEnum.LOGIN_PAGE_VIEWED,
        properties: {
          login_source: loginSource,
        },
      });
    }
  }, [open, loginSource, trackEvent]);

  const loginWithTruecaller = () => {
    /*
     * This is used to initialize the truecaller login
     * usecase: to initialize the truecaller login
     */
    const event = new CustomEvent(
      WindowEventListenersEnum.INIT_TRUECALLER_LOGIN,
    );
    window.dispatchEvent(event);
  };

  const generateRedirectionAfterLoginToUrl = () => {
    let redirectionAfterLoginToUrl = null;
    switch (loginSource as keyof typeof sourcePagesValues) {
      case sourcePagesValues[SourcePagesEnum.SEARCH]:
        redirectionAfterLoginToUrl = AppRoutesEnum.SEARCH;
        break;
      case sourcePagesValues[SourcePagesEnum.MY_LIST]:
        redirectionAfterLoginToUrl = AppRoutesEnum.MY_LIST;
        break;
      default:
        redirectionAfterLoginToUrl = null;
        break;
    }
    return redirectionAfterLoginToUrl;
  };

  const handleDrawerLoginState = (open: boolean) => {
    /*
     * This is used to pause the video player when the login drawer is opened
     * usecase: to pause the video player when the login drawer is opened
     */
    if (open) {
      videoPlayerRef.current?.pause();
      onOpenChange();
      trackEvent({
        eventName: EventNamesEnum.LOGIN_PAGE_VIEWED,
        properties: {
          login_source: loginSource,
        },
      });

      if (
        getClientSideCookieValue(cookiesValues[CookiesEnum.ShowTCByDefault]) ===
        "true"
      ) {
        loginWithTruecaller();
        const params = new URLSearchParams(window.location.search);
        const redirectionAfterLoginToUrl = generateRedirectionAfterLoginToUrl();
        if (redirectionAfterLoginToUrl) {
          params.set("redirectionAfterLoginTo", redirectionAfterLoginToUrl); // add or update query param
          router.replace(`?${params.toString()}`);
        }
      }
      return;
    }
    /*
     * This is used to play the video player when the login drawer is closed
     * usecase: to play the video player when the login drawer is closed
     */
    if (videoPlayerRef.current) {
      videoPlayerRef.current.play();
      setIsLoading(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (onDrawerOpenChange) {
      onDrawerOpenChange(isOpen);
    }
    handleDrawerLoginState(isOpen);
  };

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const loginContent = (
    <>
      <LoginForm
        isLoginDrawer={true}
        logTitle={logTitle}
        logSubTitle={logSubTitle}
      />
      <TruecallerLogin />
    </>
  );

  const triggerButton = (
    <Button className={`${isLoading ? "opacity-50" : ""}`} disabled={isLoading}>
      {isLoading ? t("common.please_wait") : label ?? t("common.login")}
    </Button>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {/*
         * This is used to trigger the login dialog
         * usecase: to trigger the login dialog
         */}
        {triggerComponent && (
          <DialogTrigger asChild>{triggerComponent}</DialogTrigger>
        )}
        {!triggerComponent && showLoginButton && (
          <DialogTrigger asChild>{triggerButton}</DialogTrigger>
        )}
        <DialogContent
          className="w-fit rounded-3xl pb-6 md:!p-0"
          preventCloseOnOutsideClick={preventCloseOnOutsideClick}
          hideCloseButton={hideCloseButton || preventCloseOnOutsideClick}
        >
          <DialogHeader className="hidden">
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>{label}</DialogDescription>
          </DialogHeader>
          {loginContent}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      onAnimationEnd={(open: boolean) => handleDrawerLoginState(open)}
      dismissible={!preventCloseOnOutsideClick}
    >
      {/*
       * This is used to trigger the login drawer
       * usecase: to trigger the login drawer
       */}
      {triggerComponent && (
        <DrawerTrigger asChild>{triggerComponent}</DrawerTrigger>
      )}
      {!triggerComponent && showLoginButton && (
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
      )}
      <DrawerContent
        className="pb-6 md:pb-16"
        hideCloseButton={hideCloseButton || preventCloseOnOutsideClick}
      >
        {/*
         * usecase: for accessibility error only
         */}
        <DrawerHeader className="hidden">
          <DrawerTitle>{label}</DrawerTitle>
          <DrawerDescription>{label}</DrawerDescription>
        </DrawerHeader>
        {loginContent}
      </DrawerContent>
    </Drawer>
  );
};
